import React from "react";
import styled from "styled-components";
import _ from "lodash";
import ProductHeaderTable from "./ProductHeaderTable";

const Product = (props) => {
  const {
    data,
    index,
    setProductDetails,
    productDetails,
    productLabels,
    firstDiscountTier,
    pathName,
    activeCompanyAudience = "",
    basketQuantity = 0
  } = props;
  const id = data.Name;
  const audience = _.get(data, "Audience", "").toLowerCase();

  if (data.Name && data.Audience && !data.RRP && !data["Pack QTY"]) {
    if (
      activeCompanyAudience
        ? !audience.includes(activeCompanyAudience.toLowerCase())
        : !audience.includes(pathName)
    ) {
      return null;
    }
    return <ProductHeaderTable productLabels={productLabels} id={id} />;
  }

  const initialQty = basketQuantity > 0 ? basketQuantity : 0;
  const netOnly =
    String(_.get(data, "Pricing", ""))
      .trim()
      .toLowerCase() === "trade";
  const tradeCost = netOnly
    ? Number(data.RRP)
    : (data.RRP / 1.2) * (1 - firstDiscountTier * 0.01);

  // SINGLE SOURCE OF TRUTH
  // productDetails[index] (held in App.js state) is the only copy of this product's quantity.
  // There is no local quantity state in Product — we read directly from productDetails[index]
  // and write back via functional setProductDetails (prev => ...) in the handlers below.
  //
  // Why no local state? A previous approach kept a local `details` state and used two useEffects
  // to sync it with productDetails in both directions. This caused an infinite update loop:
  //   local change → write to productDetails → external sync effect fires → write back to local
  //   → [details] effect fires → write to productDetails → ... and so on.
  // Removing the local copy eliminates the loop entirely.
  //
  // Why functional setProductDetails? Using `setProductDetails(prev => ...)` ensures each handler
  // always operates on the latest array, even if multiple Product components update in the same
  // render cycle. Mutating the array in place (old approach) caused TotalBox's getTotal() to read
  // stale data once we switched to immutable updates.
  const currentQty =
    productDetails[index] !== undefined
      ? productDetails[index].quantity
      : initialQty;

  // Populate productDetails[index] once on mount. The guard (prev[index] !== undefined) makes
  // this idempotent — StrictMode double-invokes effects, so without the guard we'd overwrite
  // a basket-pre-filled quantity with 0 on the second call.
  React.useEffect(() => {
    setProductDetails((prev) => {
      if (prev[index] !== undefined) return prev;
      const next = [...prev];
      next[index] = {
        name: data.Name,
        price: data.RRP,
        packQTY: data["Pack QTY"],
        tradeCost,
        netOnly,
        quantity: initialQty
      };
      return next;
    });
  }, []);

  const handleSubtract = () => {
    setProductDetails((prev) => {
      const item = prev[index];
      if (!item || item.quantity === 0) return prev;
      const next = [...prev];
      next[index] = {
        ...item,
        quantity: item.quantity - parseInt(data["Pack QTY"])
      };
      return next;
    });
  };

  const handleAdd = () => {
    setProductDetails((prev) => {
      const item = prev[index];
      if (!item) return prev;
      const next = [...prev];
      next[index] = {
        ...item,
        quantity: item.quantity + parseInt(data["Pack QTY"])
      };
      return next;
    });
  };

  const isValidImageUrl = (url) => {
    if (!url) return false;
    const imageExtensions = /\.(jpg|jpeg|png|gif|webp|svg)$/i;
    try {
      const parsedUrl = new URL(url);
      return imageExtensions.test(parsedUrl.pathname);
    } catch (e) {
      return false;
    }
  };

  if (
    !data.Name || !data.RRP || activeCompanyAudience
      ? !audience.includes(activeCompanyAudience.toLowerCase())
      : !audience.includes(pathName)
  ) {
    return null;
  }

  return (
    <StyledProductContainer>
      <StyledProduct>
        <ProductImageContainer>
          <ProductImage
            src={data["Product Image"]}
            srcSet={
              isValidImageUrl(data["Product Image"])
                ? `${data["Product Image"]} 2x`
                : "/product-image-placeholder.png"
            }
            alt={data.Name || "Product Image"}
          />
        </ProductImageContainer>
        <div>
          <ProductName dangerouslySetInnerHTML={{ __html: data.Name }} />
        </div>
        <div>
          <p className="label">{productLabels["Pack QTY"]}</p>
          <StyledCenteredP>{data["Pack QTY"]}</StyledCenteredP>
        </div>
        <div>
          <p className="label">{productLabels["RRP"]}</p>
          <p>{netOnly ? "" : `£${data.RRP}`}</p>
        </div>
        <div>
          <p className="label">{productLabels["Trade cost"]}</p>
          <p>{`£${tradeCost.toFixed(2)}`}</p>
        </div>
        <div>
          <p className="label">{productLabels["Quantity"]}</p>
          <QuantityContainer>
            <StyledIcon onClick={handleSubtract}>-</StyledIcon>
            <p>{currentQty}</p>
            <StyledIcon onClick={handleAdd}>+</StyledIcon>
          </QuantityContainer>
        </div>
      </StyledProduct>
    </StyledProductContainer>
  );
};
export default Product;

const ProductName = styled.p`
  padding-right: 20px;

  strong {
    font-weight: bold;
  }

  ul {
    padding: 0;
    list-style-position: inside;
  }

  span,
  a {
    color: #e4032d;
  }

  a {
    text-decoration: underline;
  }

  @media (max-width: 540px) {
    font-size: 18px !important;
  }
`;

const StyledProductContainer = styled.div`
  padding: 0 20px;
  background-color: #fff;
`;

const StyledProduct = styled.div`
  display: grid;
  grid-template-columns:
    120px
    calc((100% - 120px) * 2 / 6)
    calc((100% - 120px) * 1 / 6)
    calc((100% - 120px) * 1 / 6)
    calc((100% - 120px) * 1 / 6)
    calc((100% - 120px) * 1 / 6);
  table-layout: fixed; /* Ensures consistent column widths */
  width: 100%;
  color: #718393;
  padding-bottom: 20px;
  padding-top: 20px;
  align-items: center;
  grid-template-rows: 1fr;
  gap: 0px 0px;
  grid-auto-flow: row;
  grid-template-areas: ". . . .";
  border-bottom: solid 1px #e2e2e2;
  overflow: hidden; /* Prevents content from overflowing */
  max-width: 100%; /* Ensures the grid does not exceed its container */
  box-sizing: border-box; /* Includes padding and border in width/height calculations */

  p {
    margin-top: 0;
    margin-bottom: 0;
    overflow-wrap: break-word; /* Prevents long text from overflowing */
    word-wrap: break-word;
    word-break: break-word;
    max-width: 100%; /* Ensures text does not exceed its container */
  }

  .label {
    display: none;
  }

  @media (max-width: 540px) {
    display: flex;
    flex-direction: column; /* Stack items vertically */
    align-items: flex-start; /* Align items to the left */
    gap: 20px;

    & > div {
      display: flex;
      flex-direction: column;
      align-items: flex-start; /* Align items to the left */
      gap: 8px;

      p {
        margin: 0;
        font-size: 14px;
        color: #333;
        text-align: left; /* Ensure text is left-aligned */
      }

      .label {
        font-weight: bold;
        color: #e4032d;
        text-transform: uppercase;
        font-size: 12px;
      }
    }
  }
`;

const StyledCenteredP = styled.p`
  width: 60px;
  text-align: center;

  @media (max-width: 540px) {
    text-align: left; /* Align text to the left on mobile */
  }
`;

const StyledIcon = styled.button`
  color: #e4032d;
  background-color: transparent;
  margin-left: 5px;
  margin-right: 5px;
  padding: 10px;
  cursor: pointer;
  border: none;

  @media (max-width: 540px) {
    color: #fff; /* White text */
    background-color: #e4032d; /* Red background */
    border-radius: 50%; /* Make the button rounded */
    width: 32px; /* Set a fixed width for the button */
    height: 32px; /* Set a fixed height for the button */
    display: flex;
    align-items: center;
    justify-content: center; /* Center the content inside the button */

    &:hover {
      background-color: #c10226; /* Slightly darker red on hover */
    }
  }
`;

const QuantityContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center; /* Center the content */
  width: 100%;
  max-width: 100%;
  box-sizing: border-box; /* Prevents overflow due to padding */
`;

const ProductImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-right: 20px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box; /* Ensures padding is included in width calculations */

  @media (max-width: 540px) {
    width: 100%; /* Ensure it doesn't exceed the container */
    max-width: 100%;
    padding: 20px;
    background: #feeeea;
    border-radius: 8px;
    align-items: center !important;
  }
`;

const ProductImage = styled.img`
  width: auto;
  max-width: 100%;
  height: auto;
  object-fit: cover;
  border-radius: 4px;

  @media (max-width: 540px) {
    padding-right: 0;
  }
`;
